import RootStore from '@App/stores/root-store'
import * as workAPI from '@App/api/routes/work'
import { action, observable, runInAction, makeObservable } from 'mobx'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { findWorkByLocalId } from '@App/helpers/find-work-by-local-id'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { WorkModel } from '@Entities/work/work'
import { BulkUploadErrorType } from '@App/enum/bulk-upload-error-type'
import { WorksFromUpload } from '@Entities/work/works-from-upload'

export default class BulkUploadStore {
    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        makeObservable<
            BulkUploadStore,
            | '_workFileDraftLocal'
            | '_allWorksFromUpload'
            | '_worksFromUpload'
            | '_currentFileId'
            | '_worksSubmissionsCurrent'
            | '_worksErrorFromUploadPaginationInfo'
            | '_worksCompleteFromUploadPaginationInfo'
            | '_modalErrorType'
            | '_modalErrorMessage'
            | '_startTitlePositionWorkSubmissions'
        >(this, {
            _workFileDraftLocal: observable,
            _allWorksFromUpload: observable,
            _worksFromUpload: observable,
            _currentFileId: observable,
            _worksSubmissionsCurrent: observable,
            _worksErrorFromUploadPaginationInfo: observable,
            _worksCompleteFromUploadPaginationInfo: observable,
            _modalErrorType: observable,
            _modalErrorMessage: observable,
            _startTitlePositionWorkSubmissions: observable,
            getCurrentWorksFromUploadFile: action,
            setInfoModalForBulkUpload: action,
            setWorkFileDraftToLocal: action,
            setAllWorksFromUploadToLocal: action,
            setPageWorksFromUploadToLocal: action,
            setCurrentFileId: action,
            setStartTitlePositionWorkSubmissions: action,
            destroyAllWorksFromUpload: action,
            destroyWorksOnCurrentPage: action,
            destroyStartTitlePositionWorkSubmissions: action,
            destroyWorkFileDraftLocal: action,
        })

        this.rootStore = rootStore
    }

    private _workFileDraftLocal: File | undefined

    private _allWorksFromUpload: WorksFromUpload | undefined

    private _worksFromUpload: WorksFromUpload | undefined

    private _currentFileId: number

    private _worksSubmissionsCurrent: WorksFromUpload | undefined

    private _worksErrorFromUploadPaginationInfo: PaginationInfo | undefined

    private _worksCompleteFromUploadPaginationInfo: PaginationInfo | undefined

    private _modalErrorType: BulkUploadErrorType | undefined

    private _modalErrorMessage: string | null

    private _startTitlePositionWorkSubmissions: number | undefined

    async saveUploadWorks(rightsHolderId: number, works: WorksFromUpload): Promise<any> {
        try {
            return await workAPI.saveUploadWorks(rightsHolderId, works)
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    async getCompleteWorksFromUpload(
        rightsHolderId: number,
        pagination: PaginationBody = {
            page: 1,
            limit: 10000,
        }
    ): Promise<void> {
        try {
            const response = await workAPI.getCompleteWorksFromUpload(rightsHolderId, this._currentFileId, pagination)

            runInAction(() => {
                this._worksSubmissionsCurrent = response.data
            })
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    async getCurrentWorksFromUploadFile(
        rightsHolderId: number,
        pagination: PaginationBody = {
            page: 1,
            limit: 1000,
        }
    ): Promise<void> {
        try {
            const file = this._workFileDraftLocal
            if (!file) {
                this.setInfoModalForBulkUpload(undefined)
                return
            }

            const allWorksFromUpload = this._allWorksFromUpload

            const { limit, page } = pagination
            const data = new FormData()
            // for the first call
            if (!allWorksFromUpload || !allWorksFromUpload) {
                data.append('file', file, file.name)
            } else {
                const fileId = allWorksFromUpload.fileId
                data.append('file', fileId.toString())
            }
            const response = await workAPI.getCurrentWorksFromUploadFile(rightsHolderId, pagination, data)
            let worksResponse = response.data
            if (page && limit) {
                worksResponse = this.getAllWorksFromUploadResponseFirstCall(worksResponse, allWorksFromUpload)
            }

            // for the first call
            if (!allWorksFromUpload || !allWorksFromUpload) {
                runInAction(() => (this._allWorksFromUpload = worksResponse))
            } else {
                this.getAllWorksFromUploadResponseNotFirstCall(allWorksFromUpload, worksResponse)
            }

            runInAction(() => {
                this._worksFromUpload = worksResponse

                if (pagination.limit && pagination.page && worksResponse) {
                    const dataForErrorWorks = calcNumPages(worksResponse.errorCount, pagination.limit, pagination.page)
                    const dataForCompleteWorks = calcNumPages(
                        worksResponse.completeCount,
                        pagination.limit,
                        pagination.page
                    )

                    this._worksErrorFromUploadPaginationInfo = dataForErrorWorks
                    this._worksCompleteFromUploadPaginationInfo = dataForCompleteWorks
                }
            })
        } catch (e) {
            this.setInfoModalForBulkUpload(e)
            throw e
        }
    }

    formatWorksForSaving(workDraftFromLocal: WorkModel) {
        let arrayWorksOnCurrentPage = this.worksFromUpload
        let arrayAllWorks = this.allWorksFromUpload

        if (!arrayWorksOnCurrentPage || !arrayAllWorks) {
            return
        }
        let newWork: WorkModel = { ...workDraftFromLocal }

        if (newWork.isComplete) {
            let completeArrayAllWorks = [...arrayAllWorks.complete]
            let completeArrayPageWorks = [...arrayWorksOnCurrentPage.complete]

            // changed old complete work to new complete
            completeArrayAllWorks = completeArrayAllWorks.map((item: WorkModel) =>
                item.localId === newWork.localId ? newWork : item
            )

            completeArrayPageWorks = completeArrayPageWorks.map((item: WorkModel) =>
                item.localId === newWork.localId ? newWork : item
            )

            arrayAllWorks = {
                ...arrayAllWorks,
                complete: [...completeArrayAllWorks],
            }

            arrayWorksOnCurrentPage = {
                ...arrayWorksOnCurrentPage,
                complete: [...completeArrayPageWorks],
            }
        } else {
            newWork = {
                ...newWork,
                isComplete: true,
            }

            // remove from error
            const newAllErrorWorks = arrayAllWorks.error.filter((item: WorkModel) => item.localId !== newWork.localId)
            const newErrorWorksPage = arrayWorksOnCurrentPage.error.filter(
                (item: WorkModel) => item.localId !== newWork.localId
            )

            // add to complete
            const newCompleteCount = arrayAllWorks.complete.length + 1
            const newCompleteCountCurrent = arrayWorksOnCurrentPage.complete.length + 1

            arrayAllWorks = {
                ...arrayAllWorks,
                error: [...newAllErrorWorks],
                errorCount: newAllErrorWorks.length,
                complete: [...arrayAllWorks.complete, newWork],
                completeCount: newCompleteCount,
            }

            arrayWorksOnCurrentPage = {
                ...arrayWorksOnCurrentPage,
                error: [...newErrorWorksPage],
                errorCount: newErrorWorksPage.length,
                complete: [...arrayWorksOnCurrentPage.complete, newWork],
                completeCount: newCompleteCountCurrent,
            }
        }
        this.setAllWorksFromUploadToLocal(arrayAllWorks)
        this.setPageWorksFromUploadToLocal(arrayWorksOnCurrentPage)
    }

    setInfoModalForBulkUpload(error: any): void {
        if (!error) {
            this._modalErrorType = undefined
            this._modalErrorMessage = null
            return
        }

        this._modalErrorType = this.getStatusCode(error)
        this._modalErrorMessage = error.message

        if (this._modalErrorType === BulkUploadErrorType.UNKNOWN) {
            throw new Error(error.message ? error.message : 'Something went wrong')
        }
    }

    setWorkFileDraftToLocal(file: File): void {
        this._workFileDraftLocal = file
        this.rootStore.workStore.setWizardStepValidation(!!this._workFileDraftLocal)
    }

    setAllWorksFromUploadToLocal(allWorks: WorksFromUpload): void {
        this._allWorksFromUpload = allWorks
    }

    setPageWorksFromUploadToLocal(works: WorksFromUpload): void {
        this._worksFromUpload = works
    }

    setCurrentFileId(fileId: number): void {
        this._currentFileId = fileId
    }

    setStartTitlePositionWorkSubmissions(position: number): void {
        this._startTitlePositionWorkSubmissions = position
    }

    get workFileDraftLocal(): File | undefined {
        return this._workFileDraftLocal
    }

    get worksErrorFromUploadPaginationInfo(): PaginationInfo | undefined {
        return this._worksErrorFromUploadPaginationInfo
    }
    get worksCompleteFromUploadPaginationInfo(): PaginationInfo | undefined {
        return this._worksCompleteFromUploadPaginationInfo
    }
    get modalErrorMessage(): string | null | undefined {
        return this._modalErrorMessage
    }
    get worksSubmissionsCurrent(): WorksFromUpload | undefined {
        return this._worksSubmissionsCurrent
    }
    get allWorksFromUpload(): WorksFromUpload | undefined {
        return this._allWorksFromUpload
    }
    get worksFromUpload(): WorksFromUpload | undefined {
        return this._worksFromUpload
    }
    get currentFileId(): number | undefined {
        return this._currentFileId
    }

    get modalErrorType(): BulkUploadErrorType | undefined {
        return this._modalErrorType
    }

    get startTitlePositionWorkSubmissions(): number | undefined {
        return this._startTitlePositionWorkSubmissions
    }

    destroyAllWorksFromUpload(): void {
        this._allWorksFromUpload = undefined
    }

    destroyWorksOnCurrentPage(): void {
        this._worksFromUpload = undefined
    }

    destroyStartTitlePositionWorkSubmissions(): void {
        this._startTitlePositionWorkSubmissions = undefined
    }

    destroyWorkFileDraftLocal = () => {
        this._workFileDraftLocal = undefined
        this.rootStore.workStore.setWizardStepValidation(false)
    }

    private getAllWorksFromUploadResponseFirstCall(
        worksResponse: WorksFromUpload,
        allWorksFromUpload?: WorksFromUpload
    ) {
        let newErrorWorks: any[] = []
        let newCompleteWorks: any[] = []
        if (allWorksFromUpload && allWorksFromUpload) {
            const works = allWorksFromUpload

            worksResponse.error.forEach((item: WorkModel) => {
                const localId = item.localId ? item.localId : '-1'
                const existingWork = findWorkByLocalId(localId, works)

                // if work with this localId exist
                if (existingWork) {
                    // if this work complete now - set to complete
                    if (existingWork.isComplete) {
                        newCompleteWorks = [...newCompleteWorks, existingWork]
                    } else {
                        // if this work error - set to error
                        newErrorWorks = [...newErrorWorks, existingWork]
                    }
                } else {
                    const newWork = { ...item }
                    newErrorWorks = [...newErrorWorks, newWork]
                }
            })

            worksResponse.complete.forEach((item: WorkModel) => {
                const localId = item.localId ? item.localId : '-1'
                const existingWork = findWorkByLocalId(localId, works)

                // if work with this localId exist
                if (existingWork) {
                    // if this work complete now - set to complete
                    if (existingWork.isComplete) {
                        newCompleteWorks = [...newCompleteWorks, existingWork]
                    } else {
                        // if this work error - set to error
                        newErrorWorks = [...newErrorWorks, existingWork]
                    }
                } else {
                    const newWork = { ...item }
                    newCompleteWorks = [...newCompleteWorks, newWork]
                }
            })
        } else {
            worksResponse.error.forEach((item: WorkModel) => {
                const newWork = { ...item }
                newErrorWorks = [...newErrorWorks, newWork]
            })

            worksResponse.complete.forEach((item: WorkModel) => {
                const newWork = { ...item }
                newCompleteWorks = [...newCompleteWorks, newWork]
            })
        }

        return {
            ...worksResponse,
            error: newErrorWorks,
            complete: newCompleteWorks,
        }
    }

    private getAllWorksFromUploadResponseNotFirstCall(
        allWorksFromUpload: WorksFromUpload,
        worksResponse: WorksFromUpload
    ) {
        let allWorks = allWorksFromUpload

        // Set current work page to array, that contain all work
        worksResponse.error.forEach((currentWork: WorkModel) => {
            const isNewWorkFromError = allWorks.error.every((work: WorkModel) => work.localId !== currentWork.localId)

            const isNewWorkFromComplete = allWorks.complete.every(
                (work: WorkModel) => work.localId !== currentWork.localId
            )

            // if this work doesn't exist
            if (isNewWorkFromError && isNewWorkFromComplete) {
                allWorks = {
                    ...allWorks,
                    error: [...allWorks.error, currentWork],
                }
            }
        })

        worksResponse.complete.forEach((currentWork: WorkModel) => {
            const isNewWork = allWorks.complete.every((work: WorkModel) => work.localId !== currentWork.localId)

            if (isNewWork) {
                allWorks = {
                    ...allWorks,
                    complete: [...allWorks.complete, currentWork],
                }
            }
        })

        runInAction(() => (this._allWorksFromUpload = allWorks))
    }

    private getStatusCode(error: any): BulkUploadErrorType {
        switch (error.statusCode) {
            case 500:
                return BulkUploadErrorType.PARSE_ERROR
            case 413:
            case 415:
                return BulkUploadErrorType.FILE_TOO_LARGE
            case 400:
                if (error.message.toLowerCase().includes('required')) {
                    return BulkUploadErrorType.REQUIRED
                }
                if (error.message.toLowerCase().includes('duplicate')) {
                    return BulkUploadErrorType.DUPLICATE_TITLE
                }
        }
        return BulkUploadErrorType.UNKNOWN
    }
}
